import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Search from '@components/LibarySearch';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import Status from '@components/LibraryStatus';
import * as ScoreCard from '@components/LibraryToDoScoreCard';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';

import { LibraryScoreCardsTabs, ToDoTypes } from '~/services/config';

const SCORECARD_TYPE = {
    daily: 0,
    monthly: 1,
    quarterly: 2,
    yearly: 3,
};

const ScoreCards = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const ref = React.useRef(queryString.parse(location.search)?.active || 'monthly');
    const dispatch = useDispatch();

    const userRole = useSelector((state) => state.app.app_role_id) ?? 1;
    const templates = useSelector((state) => state.develop.templates) ?? {};
    const companyId = useSelector((state) => state.app.company_info.id);

    const [active, setActive] = useState(queryString.parse(location.search)?.active || 'monthly');
    const [filter, setFilter] = useState(queryString.parse(location.search)?.filter || '');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    // Step 1: Memoize action dispatchers to prevent recreation on every render
    const getTemplates = useCallback((target, payload) => dispatch(DevelopActions.getlibrarytemplatessearchRequest(target, payload)), [dispatch]);
    const toggleModal = useCallback((e) => dispatch(AppActions.modalRequest(e)), [dispatch]);
    const deleteTemplate = useCallback((target, event, id, data, route, after) => dispatch(DevelopActions.libraryeventRequest(target, event, id, data, route, after)), [dispatch]);

    // Step 2: Memoize event handlers to prevent child re-renders
    const handleSearch = useCallback((e) => {
        setFilter(e.target.value);
        const url = new URL(window.location);
        url.searchParams.set('filter', e.target.value);
        window.history.pushState({}, '', url);
    }, []);

    const handleTab = useCallback((e) => {
        navigate(`?active=${e.name}`);
        setActive(e.name);
        ref.current = e.name;
        setFilter('');
        setPage(1);
    }, [navigate]);

    const handleModal = useCallback((e) => toggleModal(e), [toggleModal]);

    const handleSelectMenu = useCallback(
        (event, card) => {
            const disabled = ToDoTypes.filter((x) => x !== active);

            switch (event) {
                case 'detail view': {
                    navigate({
                        pathname: `/library/scorecards/${card.id}/view`,
                        state: { filter, page },
                    });
                    break;
                }
                case 'edit': {
                    navigate({
                        pathname: `/library/scorecards/${card.id}/edit`,
                        state: { editable: true },
                    });
                    break;
                }
                case 'delete':
                    toggleModal({
                        type: 'Confirm',
                        data: { before: { title: 'Delete', body: 'Are you sure you want to delete this item?' } },
                        callBack: { 
                            onYes: () => {
                                // Get fresh values at the time of deletion
                                deleteTemplate('scorecard', 'delete', card.id, null, null, {
                                    type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                                    target: 'scorecards',
                                    payload: { 
                                        search: filter, 
                                        page, 
                                        perPage, 
                                        type: [SCORECARD_TYPE[active]] 
                                    },
                                });
                            }
                        },
                    });
                    break;
                case 'assign': {
                    const payload = {
                        type: 'Assign ScoreCard',
                        data: { 
                            before: { modules: [card], disabled, active: ref.current }, 
                            after: []
                        },
                        callBack: {
                            onSearch: (target, searchParams) => {
                                getTemplates(target, searchParams);
                            },
                            onfetchEmployeesSearch: (searchParams) => {
                                dispatch(AppActions.getemployeesearchRequest(searchParams));
                            }
                        }
                    };
                    
                    toggleModal(payload);
                    break;
                }
                case 'duplicate':
                    toggleModal({
                        type: 'Duplicate Module',
                        data: { before: { type: 'scorecards', object: card } },
                    });
                    break;
                case 'quick assign': {
                    toggleModal({
                        type: 'Quick Edit',
                        data: { before: { template: card, type: 'scorecards', from: 'template', companyId } },
                        callBack: { 
                            onDelete: () => {
                                // Get fresh values at the time of deletion
                                deleteTemplate('scorecard', 'delete', card.id, null, null, {
                                    type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                                    target: 'scorecards',
                                    payload: { 
                                        search: filter, 
                                        page, 
                                        perPage, 
                                        type: [SCORECARD_TYPE[active]] 
                                    },
                                });
                            }
                        },
                    });
                    break;
                }
                default:
                    break;
            }
        },
        [filter, page, perPage, companyId, active, navigate, toggleModal, deleteTemplate, dispatch, getTemplates] // Complete dependencies
    );

    const handleToggle = useCallback(
        (id) => {
            navigate({
                pathname: `/library/scorecards/${id}/view`,
                state: { filter, page },
            });
        },
        [filter, page, navigate]
    );

    const handleAddScorecard = useCallback(() => {
        toggleModal({ type: 'Add Scorecard' });
    }, [toggleModal]);

    const handleChangePage = useCallback((page) => {
        setPage(page);
    }, []);

    const handlePer = useCallback((perPage) => {
        setPerPage(perPage);
    }, []);

    // Step 3: Optimize useEffect to prevent unnecessary API calls during modal state changes
    const effectDeps = useMemo(() => ({ filter, page, perPage, active }), [filter, page, perPage, active]);
    const prevDeps = React.useRef(null); // Start with null to ensure initial load

    useEffect(() => {
        // Always run on initial load (when prevDeps is null) or when dependencies changed
        const isInitialLoad = prevDeps.current === null;
        const depsChanged = !isInitialLoad && JSON.stringify(prevDeps.current) !== JSON.stringify(effectDeps);
        
        if (isInitialLoad || depsChanged) {
            getTemplates('scorecards', { search: filter, page, perPage, type: [SCORECARD_TYPE[active]] });
            prevDeps.current = effectDeps;
        }
    }, [effectDeps, getTemplates, filter, page, perPage, active]);

    return (
        <ErrorBoundary>
            <Filter add="Scorecard" onAdd={handleAddScorecard} />
            <div className="card px-0 px-sm-4">
                <div className="d-h-between ai-start">
                    <div className="d-flex flex-column w-75">
                        <p className="dsl-b22 bold mb-3">Scorecard</p>
                        <ResponsiveTabs active={active} data={LibraryScoreCardsTabs} getId={(e) => e['name']} getValue={(e) => e['label']} onTab={handleTab} />
                    </div>
                    <Search initial={filter} type={active} onChange={handleSearch} />
                </div>
                {LibraryScoreCardsTabs.map((tab) => (
                    <Tab key={tab.id} eventKey={tab.name} active={active}>
                        <Status type="Scorecards" showToggle counts={templates['scorecards']?.total} tracking={SCORECARD_TYPE[active]} />
                        <ScoreCard.List
                            role={userRole}
                            data={templates['scorecards']}
                            onChange={handleChangePage}
                            onPer={handlePer}
                            onModal={handleModal}
                            onSelect={handleSelectMenu}
                            onToggle={handleToggle}
                            onAdd={handleAddScorecard}
                        />
                    </Tab>
                ))}
            </div>
        </ErrorBoundary>
    );
};

export default ScoreCards;
